import React, { Component } from 'react'
import ProgressBarIndicator from '@components/ProgressBarIndicator'
import PropTypes from 'prop-types'
import { inject, observer } from 'mobx-react'
import { Formik, Form, Field } from 'formik'
import yup from 'yup'
import flatten from 'lodash/flatten'
import pick from 'lodash/pick'

import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import Input from '@components/Form/Input'
import {
  Autocomplete,
  Dropdown,
  TagsInput
} from '@components/Form/Autocomplete'
import RichTextEditor from '@components/Form/RichTextEditor'
import Button from '@components/Button'
import Spinner from '@components/spinner'
import Attachment from '@components/Attachment'

import { searchEmployees, fileApi } from '@atheneum/api'
import { getGlobalTemplateType } from '../MassProcessing/components/actions/helper'

class EmailTemplatesForm extends Component {
  static propTypes = {
    modalStore: PropTypes.object,
    growlStore: PropTypes.object,
    emailTemplatesStore: PropTypes.object,
    group: PropTypes.string.isRequired,
    name: PropTypes.string,
    loadTemplateUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
      .isRequired,
    sendTemplateUrl: PropTypes.string.isRequired,
    onSend: PropTypes.func,
    progress: PropTypes.object,
    onBeforeSend: PropTypes.func,
    onAfterSend: PropTypes.func,
    loadToOptions: PropTypes.func,
    loadCcOptions: PropTypes.func,
    title: PropTypes.string.isRequired,
    showAttachments: PropTypes.bool,
    senderIds: PropTypes.arrayOf(PropTypes.number)
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: true,
    wideContent: true,
    loadCcOptions: query =>
      searchEmployees({
        q: query,
        accounting: true,
        employeeStatus: 'active'
      }).then(results =>
        results.rows.map(employee => {
          employee.name = employee.fullName
          return employee
        })
      ),
    onBeforeSend: () => {},
    title: 'Send email',
    showAttachments: false
  }

  constructor(props) {
    super(props)

    const { emailTemplatesStore, loadTemplateUrl, senderIds } = props

    emailTemplatesStore
      .getTemplateOptions(props.group)
      .then(templates => {
        const defaultOption = templates.find(({ groupDefault }) => groupDefault)
        const templateName = props.name || (defaultOption && defaultOption.name)

        if (templateName) {
          emailTemplatesStore.getTemplateData(loadTemplateUrl, templateName)
        }

        if (senderIds) {
          emailTemplatesStore.getSenders(senderIds)
        }
      })
      .catch(err => {
        this.props.growlStore.showError(err.message)
      })
  }

  handleTemplateChange = (template, form) => {
    const { emailTemplatesStore, loadTemplateUrl } = this.props

    emailTemplatesStore
      .getTemplateData(loadTemplateUrl, template.name)
      .then(() => {
        const { content, subject, placeholders, to, cc } =
          emailTemplatesStore.template
        form.setFieldValue('template', template, true)
        form.setFieldValue('content', content, true)
        form.setFieldValue('subject', subject, true)
        form.setFieldValue('to', to.slice(), true)
        form.setFieldValue('placeholders', placeholders, false)
        form.setFieldValue('cc', cc.slice(), true)
      })
      .catch(err => {
        this.props.growlStore.showError(err.message)
      })
  }

  handleTemplateTypeChange = (template, form) => {
    localStorage.setItem('templateType', JSON.stringify(template))
    form.setFieldValue('templateType', template, true)
  }

  handleSubmit = (values, actions) => {
    const { emailTemplatesStore, sendTemplateUrl, onSend, onBeforeSend } =
      this.props

    const data = pick(values, ['subject', 'content', 'to', 'cc', 'senderId'])
    data.templateName = values.template.name
    data.templateType = values.templateType.name

    if (this.props.showAttachments) {
      data.files = values.attachments.map(({ id }) => id)
    }

    const onAfterSend =
      this.props.onAfterSend ||
      (() => {
        this.props.modalStore.close()
        this.props.growlStore.showSuccess('Email sent')
      })

    return Promise.resolve(onBeforeSend(data))
      .then(result =>
        onSend
          ? onSend(data, result)
          : emailTemplatesStore.sendTemplate(sendTemplateUrl, data)
      )
      .then(result => onAfterSend(result, data))
      .catch(({ message }) => {
        this.error = message
        actions.setSubmitting(false)
      })
  }

  getValidationSchema = () => {
    const recipient = yup.object().shape({
      email: yup.string().required(),
      name: yup.string()
    })

    const schema = {
      subject: yup
        .string()
        .required('Subject field is required')
        .typeError('Subject field is required'),
      content: yup
        .string()
        .required('Content field is required')
        .typeError('Content field is required'),
      to: yup
        .array()
        .of(recipient)
        .required('To field is required')
        .typeError('To field is required'),
      cc: yup.array().of(recipient)
    }

    if (this.props.senderIds) {
      schema.senderId = yup
        .number()
        .integer()
        .nullable()
        .required('Sender is required')
    }

    return yup.object().shape(schema)
  }

  getInitialValues = () => {
    const { template, templateOptions, senders } =
      this.props.emailTemplatesStore

    const initialValues = {
      ...template,
      to: template.to.slice(),
      template: templateOptions.find(option => option.name === template.name),
      templateType: getGlobalTemplateType(),
      attachments: template.attachments ? template.attachments.slice() : [],
      cc: template.cc.slice()
    }

    if (this.props.senderIds && senders.length) {
      initialValues.senderId = senders[0].id
    }

    return initialValues
  }

  renderAttachments = form => {
    const { template } = this.props.emailTemplatesStore
    if (this.props.showAttachments) {
      return (
        <div className="expert-form__section">
          <Attachment
            attachments={form.values.attachments}
            tooltip="Click to add attachments"
            onOk={({ data }) => {
              const attachments = form.values.attachments
              attachments.push(data[0])
              form.setFieldValue('attachments', attachments, false)
              form.setFieldTouched('attachments', true)
            }}
            onDelete={fileId => {
              const isEmailAttachment = template.attachments.every(
                att => att.id !== fileId
              )

              if (isEmailAttachment) fileApi.deleteFile(fileId)

              const attachments = form.values.attachments.filter(
                atch => atch.id !== fileId
              )
              form.setFieldValue('attachments', attachments, false)
              form.setFieldTouched('attachments', true)
            }}
            label="Add attachment"
            url="/api/file"
          />
        </div>
      )
    }

    return null
  }

  renderSpinnerOverlay = () => {
    const { loadingTemplateOptions, loadingTemplateData } =
      this.props.emailTemplatesStore

    if (loadingTemplateOptions || loadingTemplateData) {
      return (
        <div className="spinner-overlay">
          <Spinner />
        </div>
      )
    }

    return null
  }

  render() {
    const {
      loadToOptions,
      loadCcOptions,
      emailTemplatesStore,
      modalStore,
      progress = { submitting: false },
      title,
      senderIds
    } = this.props
    const { template, senders, loadingSenders, loadingTemplateOptions } =
      emailTemplatesStore

    const { submitting, processed } = progress

    const templateOptions = emailTemplatesStore.templateOptions.slice()
    const templateTypeOptions = emailTemplatesStore.templateTypeOptions.slice()

    if (loadingSenders || loadingTemplateOptions) return <Spinner />

    return (
      <div className="email-template-form-wrapper">
        {this.renderSpinnerOverlay()}
        <Formik
          enableReinitialize
          initialValues={this.getInitialValues()}
          validationSchema={this.getValidationSchema()}
          onSubmit={this.handleSubmit}
          render={form => (
            <div>
              <div className="expert-form">
                <Form onSubmit={form.handleSubmit}>
                  <h2 className="expert-form__subtitle">{title}</h2>
                  {this.error ? (
                    <InfoBox theme="red" icon={<WarningIcon />}>
                      {this.error}
                    </InfoBox>
                  ) : null}
                  <div className="expert-form__section">
                    <Field
                      name="template"
                      label="Template"
                      showResultsOnEmptyInput
                      component={Autocomplete}
                      options={templateOptions}
                      itemToString={item => item && item.name}
                      onChange={item => this.handleTemplateChange(item, form)}
                    />
                    <Field
                      name="templateType"
                      label="Template Type"
                      showResultsOnEmptyInput
                      component={Autocomplete}
                      options={templateTypeOptions}
                      itemToString={item => item && item.name}
                      onChange={item => this.handleTemplateTypeChange(item, form)}
                    />
                    {senderIds ? (
                      <Field
                        name="senderId"
                        label="Sender"
                        component={Dropdown}
                        itemToString={item => item && item.label}
                        value={senders.find(
                          s => s.value === form.values.senderId
                        )}
                        options={senders}
                      />
                    ) : null}
                    <Field
                      name="to"
                      label="To"
                      component={TagsInput}
                      disabled={!loadToOptions}
                      itemToString={item => item && (item.name || item.email)}
                      loadOptions={combineRecipients(
                        template.to,
                        loadToOptions
                      )}
                    />
                    <Field
                      name="cc"
                      label="Cc"
                      component={TagsInput}
                      itemToString={item => item && (item.name || item.email)}
                      loadOptions={combineRecipients(
                        template.cc,
                        loadCcOptions
                      )}
                    />
                    <Field name="subject" label="Subject" component={Input} />
                    <Field
                      name="content"
                      label="Content"
                      component={RichTextEditor}
                    />
                  </div>
                  {this.renderAttachments(form)}
                  <div className="expert-form__section">
                    <div style={{ marginBottom: 15 }}>
                      {progress.submitting && (
                        <ProgressBarIndicator
                          target={progress.valid}
                          theme="blue"
                          delivered={progress.processed}
                          label="Creating EPL's"
                          percent={Math.ceil(
                            ((progress.processed + progress.failed) /
                              progress.valid) *
                              100
                          )}
                        />
                      )}
                    </div>
                    <Button
                      primary
                      submit
                      onClick={this.onSubmit}
                      disabled={form.isSubmitting}>
                      Send
                    </Button>
                    <Button
                      onClick={() => {
                        modalStore.close()
                      }}>
                      Cancel
                    </Button>
                    <Button
                      className="pull-right"
                      onClick={() => {
                        this.handleTemplateChange(form.values.template, form)
                      }}>
                      reset template
                    </Button>
                  </div>
                </Form>
                <InfoBox className="placeholders" icon={null}>
                  <h3>Placeholders</h3>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: form.values.placeholders
                    }}
                  />
                </InfoBox>
              </div>
            </div>
          )}
        />
      </div>
    )
  }
}

function combineRecipients(recipients, loadFn) {
  return query => {
    const recipientsToCombine = [recipients.slice()]

    if (loadFn) {
      recipientsToCombine.push(loadFn(query))
    }

    return Promise.all(recipientsToCombine).then(recipients =>
      flatten(recipients)
    )
  }
}

export default inject(
  'modalStore',
  'growlStore',
  'emailTemplatesStore'
)(observer(EmailTemplatesForm))
